import { useEffect, useMemo } from 'react'
import { Navigate, useLocation } from 'react-router-dom'

import { getIsFetchingOrNotStarted } from '@/reducers/fetch'
import {
  getCurrentUser,
  getIsRequiredStepsOnboarding,
  selectIsInUnservicedFreeTrial,
  selectIsSigningUp,
} from '@/selectors/user.selectors'

import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'

import { SIGNUP_PATHS } from '@/features/user/signup/helpers'
import { FETCH_USER_CATCH_UP_BOOKKEEPING_STATUS } from '@/components/CatchUpBookkeepingStatus/catchUpBookkeepingStatus.slice'

import { MainDashboard } from './MainDashboard'
import {
  FETCH_USER_ONBOARDING_STEPS_KEY,
  fetchUserOnboardingStepsIfNeeded,
} from '@/features/onboarding/user-onboarding-steps/onboarding.actions'
import { useSelectUserShouldSeeOnboardingDashboard } from '@/features/onboarding/user-onboarding-steps/onboarding.hooks'
import { Loader } from '@/ui'

export const Dashboard = () => {
  const dispatch = useAppDispatch()
  const { search } = useLocation()
  const currentUser = useReselector(getCurrentUser)
  const isRequiredStepsOnboarding = useReselector(getIsRequiredStepsOnboarding)
  const isSigningUp = useReselector(selectIsSigningUp)

  const shouldShowOnboardingDashboard =
    useSelectUserShouldSeeOnboardingDashboard()

  const catchUpBKStatusIsLoading = useReselector(
    getIsFetchingOrNotStarted,
    FETCH_USER_CATCH_UP_BOOKKEEPING_STATUS
  )
  const onboardingStepsIsLoading = useReselector(
    getIsFetchingOrNotStarted,
    FETCH_USER_ONBOARDING_STEPS_KEY
  )
  const isInUnservicedFreeTrial = useReselector(selectIsInUnservicedFreeTrial)

  useEffect(() => {
    if (!currentUser?.admin) {
      dispatch(
        fetchUserOnboardingStepsIfNeeded({
          expanded: true,
        })
      )
    }
  }, [currentUser?.admin, currentUser?.id, dispatch])

  const renderRelevantDashboard = useMemo(() => {
    if (catchUpBKStatusIsLoading || onboardingStepsIsLoading) {
      return <Loader active />
      // Show OB Dashboard for Free Trial — 14 days
    } else if (isInUnservicedFreeTrial || shouldShowOnboardingDashboard) {
      return <Navigate to={{ pathname: '/onboarding-dashboard', search }} />
    } else {
      return <MainDashboard />
    }
  }, [
    catchUpBKStatusIsLoading,
    onboardingStepsIsLoading,
    shouldShowOnboardingDashboard,
    isInUnservicedFreeTrial,
    search,
  ])

  // If we don't have a user, assume we're still loading
  if (!currentUser) {
    return <Loader active />
  }

  // Check if we need to navigate away from the dashboard
  if (currentUser?.admin) {
    // If admin, we want to redirect to the admin dashboard
    return <Navigate to="/admin/finances" />
  } else if (isSigningUp) {
    // Check if user is in the middle of signing up or resigning up.
    // If so, redirect them to continue. Carrying the query string forward so a
    // link like ?trial=freetrial2026 survives the bounce back into signup.
    return <Navigate to={`${SIGNUP_PATHS.aboutYourPractice}${search}`} />
  } else if (isRequiredStepsOnboarding) {
    // Check if user is in the locked down onboarding flow
    return <Navigate to={'/onboarding-v2'} />
  }
  return renderRelevantDashboard
}
